import type { History, Location } from "history";

import { isRemixOwnedPath } from "./remixOwnedPaths";

/**
 * Attaches a `history.listen` callback that intercepts client-side
 * navigations to Remix-owned paths and performs a document navigation
 * (`window.location.assign`) instead. This lets nginx forward the request to
 * the fs-node Remix sidecar rather than letting the current SPA's router try
 * to render a route it doesn't own.
 *
 * This is the Rails → Remix half of the cross-app navigation handoff. It is
 * the single source of truth shared by both the clinic-specific
 * `clinicBrowserHistory` and the shared `BrowserRouter`, so the two never
 * diverge.
 *
 * Returns the `unlisten` function so callers with a lifecycle (e.g. a React
 * effect) can detach it on cleanup.
 */
const attachRemixHandoff = (history: History): (() => void) => {
  let prevLocation: Location | null = null;

  return history.listen(({ location }) => {
    if (!isRemixOwnedPath(location.pathname)) return;
    if (prevLocation?.pathname === location.pathname) return;

    prevLocation = location;

    const target = `${location.pathname}${location.search}${location.hash}`;

    window.location.assign(target);
  });
};

export { attachRemixHandoff };
